/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext, useEffect } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import CheckboxList from '../../../customs/checkboxList/CheckboxList';
import {
  getSymptomStartNameTuplesFor,
  SymptomStartKeys,
} from '../../../../models/genericElements/operationalData/symptomStart';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';

import { InputState } from '../../../../backendModels/general.model';
import { NodeType } from '../../../../backendModels/report.model';
import { Draft } from 'immer';
import { useFreshCallback } from '../../../../utils/hooks';
import { isGenericRecordDeletable } from '../../../../models/generic';
import {
  changePropertyAndMarkTouched,
  getTouchedClassName,
  handleUntouchedTextFieldUpdates,
} from '../../../../utils/recordWithTouched';
import { nonEmptyString, timeHHmmRegex } from '../../../../utils/util';
import {
  SYMPTOM_ONSET_RECORD_ELEMENT_KEY,
  SymptomOnsetPropertyKeys,
  SymptomOnsetRecord,
} from '../../../../models/genericElements/operationalData/symptomOnset';
import { TimePicker } from '../../../customs/timePicker/TimePicker';
import _ from 'lodash';
import { ApplicationConfig } from '../../../../config/application.config';

interface SymptomStartProps {
  nodeType: NodeType;
}

const symptomStartItems = getSymptomStartNameTuplesFor([
  SymptomStartKeys.ueber24H,
  SymptomStartKeys.beobachtet,
  SymptomStartKeys.unbekannt,
]);

export default function SymptomStart({ nodeType }: SymptomStartProps) {
  const { findRecordOrDefault, adaptRecord, patient, patientUpdate, findNodes } = useContext(ReportsAPIContext);
  const record = findRecordOrDefault('generic', nodeType, SYMPTOM_ONSET_RECORD_ELEMENT_KEY) as SymptomOnsetRecord;

  useEffect(() => {
    if (patient != null && findNodes(nodeType).length > 0) {
      adaptRecord(
        'generic',
        nodeType,
        (draft: Draft<SymptomOnsetRecord>, deleteRecord) => {
          draft.inputState = InputState.ENTERED;

          handleUntouchedTextFieldUpdates(
            draft,
            SymptomOnsetPropertyKeys.symptomOnsetTimestamp,
            patient.symptomOnset && timeHHmmRegex.test(patient.symptomOnset) ? patient.symptomOnset : undefined
          );

          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        },
        SYMPTOM_ONSET_RECORD_ELEMENT_KEY
      );
    }
  }, [patient, findNodes, nodeType, adaptRecord]);

  const selectedSymptomStartValues =
    record.inputState === InputState.ENTERED ? (record.values?.symptomOnsetOptions?.options ?? []) : [];
  const timeString = record.inputState == InputState.ENTERED ? (record.values.symptomOnsetTimestamp?.text ?? '') : '';

  const handleSymptomStartChange = (values: SymptomStartKeys[]) => {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      SYMPTOM_ONSET_RECORD_ELEMENT_KEY,
      SymptomOnsetPropertyKeys.symptomOnsetOptions,
      values.length !== 0 ? { fieldType: 'multiSelect', options: values } : undefined
    );
  };

  const handleStartTimeChange = useFreshCallback((value: string) => {
    changePropertyAndMarkTouched(
      adaptRecord,
      nodeType,
      SYMPTOM_ONSET_RECORD_ELEMENT_KEY,
      SymptomOnsetPropertyKeys.symptomOnsetTimestamp,
      value !== ''
        ? {
            fieldType: 'text',
            text: value,
          }
        : undefined
    );
  });

  function handleStartTimeSave(valueToSave: string | undefined) {
    if (nonEmptyString(valueToSave)) {
      patientUpdate?.({ ...patient, symptomOnset: valueToSave });
    } else {
      patientUpdate?.(_.omit(patient, ['symptomOnset']));
    }
  }

  return (
    <DiviFieldCard cardType={CardId.SymptomStart}>
      <TimePicker
        value={timeString}
        onChange={handleStartTimeChange}
        onUnfocus={ApplicationConfig.immediatelySyncToConference ? handleStartTimeSave : undefined}
        label='Symptom Beginn (z.B. Kollaps Unfallzeitpunkt)'
        className={`time-of-arrival ${getTouchedClassName(record, SymptomOnsetPropertyKeys.symptomOnsetTimestamp)}`}
      />
      <CheckboxList
        className='add-margin-top symptoms-list'
        items={symptomStartItems}
        selectedValues={selectedSymptomStartValues}
        onValuesChange={handleSymptomStartChange}
      />
    </DiviFieldCard>
  );
}
